// Hero canvas — variant 5: floating translucent glass shards.
import React, { Suspense, useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Float, Environment } from "@react-three/drei";
import * as THREE from "three";
import { useTheme } from "../../contexts/ThemeContext";

const h = React.createElement;

function Shards({ mouse, isDark }) {
  const grp = useRef();
  const shards = useMemo(() => {
    const rand = (a, b) => a + Math.random() * (b - a);
    return Array.from({ length: 9 }, () => ({
      pos: [rand(-2.4, 2.4), rand(-1.6, 1.6), rand(-2.2, 1)],
      rot: [rand(-1, 1), rand(-1, 1), rand(-1, 1)],
      scale: [rand(1.4, 2.6), rand(2.0, 3.6), 0.04],
      speed: rand(0.05, 0.18),
      phase: Math.random() * Math.PI * 2,
    }));
  }, []);

  useFrame((state) => {
    if (!grp.current) return;
    const t = state.clock.getElapsedTime();
    grp.current.rotation.x = THREE.MathUtils.lerp(grp.current.rotation.x, mouse.current.y * 0.25, 0.04);
    grp.current.rotation.y = THREE.MathUtils.lerp(grp.current.rotation.y, mouse.current.x * 0.35 + t * 0.04, 0.04);
    grp.current.children.forEach((m, i) => {
      const s = shards[i];
      m.position.y += Math.sin(t * s.speed * 3 + s.phase) * 0.0015;
      m.rotation.z += s.speed * 0.004;
    });
  });

  return h(
    "group",
    { ref: grp },
    shards.map((s, i) =>
      h(
        "mesh",
        { key: i, position: s.pos, rotation: s.rot, scale: s.scale },
        h("boxGeometry", { args: [1, 1, 1] }),
        h("meshPhysicalMaterial", {
          color: isDark ? "#ffffff" : "#27272a",
          metalness: 0.1,
          roughness: 0.08,
          transmission: 0.92,
          thickness: 0.6,
          ior: 1.4,
          clearcoat: 1,
          clearcoatRoughness: 0.05,
          attenuationColor: isDark ? "#94a3b8" : "#a1a1aa",
          attenuationDistance: 2.5,
          envMapIntensity: 1.4,
          opacity: 0.95,
          transparent: true,
        }),
      ),
    ),
  );
}

function Scene({ mouse, isDark }) {
  const bg = isDark ? "#050505" : "#fafafa";
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: [bg] }),
    h("fog", { attach: "fog", args: [bg, 6, 16] }),
    h("ambientLight", { intensity: isDark ? 0.25 : 0.65 }),
    h("directionalLight", { position: [5, 6, 4], intensity: 1.2 }),
    h("directionalLight", { position: [-4, -2, 3], intensity: 0.7, color: "#e2e8f0" }),
    h(
      Float,
      { speed: 0.8, rotationIntensity: 0.15, floatIntensity: 0.5 },
      h(
        Suspense,
        { fallback: null },
        h(Shards, { mouse, isDark }),
        h(Environment, { preset: isDark ? "city" : "studio" }),
      ),
    ),
  );
}

export default function HeroCanvasShards() {
  const mouse = useRef({ x: 0, y: 0 });
  const { theme } = useTheme();
  const isDark = theme === "dark";

  const onPointer = (e) => {
    const x = (e.clientX / window.innerWidth) * 2 - 1;
    const y = -(e.clientY / window.innerHeight) * 2 + 1;
    mouse.current.x = x;
    mouse.current.y = y;
  };

  return (
    <div
      className="absolute inset-0 -z-0"
      onPointerMove={onPointer}
      data-testid="hero-canvas-shards"
      aria-hidden="true"
    >
      <Canvas
        key={theme}
        dpr={[1, 1.6]}
        camera={{ position: [0, 0, 6], fov: 48 }}
        gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
      >
        <Scene mouse={mouse} isDark={isDark} />
      </Canvas>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: isDark
            ? "radial-gradient(ellipse at center, transparent 30%, rgba(5,5,5,0.85) 88%)"
            : "radial-gradient(ellipse at center, transparent 30%, rgba(250,250,250,0.85) 88%)",
        }}
      />
    </div>
  );
}
